import { format } from '@visx/vendor/d3-format';
import { Rate, UnitPriceType } from './line-item-channels';
import { isNil } from 'lodash';
import { PartialLineItem } from 'shared/src/domain/line-item/line-item-types';
import { PacingBlockV2 } from 'shared/src/api/line-item-types';

export const formatNumber = format('.3~s');
export const formatPercentage = (value: number, precision: number = 1) =>
  format(`,.${precision}%`)(value);
export const formatSpend = format('$,.2f');

type LineItemMetricsFields = {
  unit_price?: number | null | undefined;
  unit_price_type?:
    | {
        id: number;
        name: 'CPM' | 'CPC' | 'CPV' | 'CPCV' | 'CPA' | 'CPL' | 'Flat Fee';
      }
    | undefined;
  referral_commission_rate?: number | null | undefined;
  agency_markup_rate?: number | null | undefined;
  planned_margin?: number | null | undefined;
};

export function calcEffectiveUnitPrice(
  lineItem: LineItemMetricsFields,
  referralCommissionRate?: number | undefined | null
): number | undefined {
  const { unit_price } = lineItem;
  if (unit_price == null) return undefined;
  const effectiveRate = referralCommissionRate ?? lineItem.referral_commission_rate ?? 0;
  return unit_price * (1 - effectiveRate);
}

export function calcEstimatedUnits(
  lineItem: PartialLineItem,
  referralCommissionRate?: number | undefined | null
) {
  if (lineItem.line_item_type === 'zero_dollar') return lineItem.estimated_units;
  if (lineItem.line_item_type === 'management_fee') {
    const { planned_unit_cost } = lineItem;
    if (isNil(planned_unit_cost) || planned_unit_cost === 0) return undefined;

    // Calculate media budget if not available or if calculated value differs
    // Note: We don't call calcMediaBudget here to avoid circular dependency
    // Instead, we use the stored media_budget value directly
    const { media_budget } = lineItem;
    if (isNil(media_budget)) return undefined;

    return Math.round(media_budget / planned_unit_cost);
  }

  const effectivePrice = calcEffectivePrice(lineItem, referralCommissionRate);
  const effectiveUnitPrice = calcEffectiveUnitPrice(lineItem, referralCommissionRate);
  return effectivePrice && effectiveUnitPrice && effectiveUnitPrice > 0
    ? Math.round(effectivePrice / effectiveUnitPrice)
    : undefined;
}

export function getAdjustedEffectiveUnitPrice(
  lineItem: LineItemMetricsFields,
  referralCommissionRate?: number | undefined | null
) {
  const effective_unit_price = calcEffectiveUnitPrice(lineItem, referralCommissionRate);
  if (effective_unit_price == null) return undefined;
  return lineItem.unit_price_type?.name === 'CPM'
    ? effective_unit_price * 1000
    : effective_unit_price;
}

export function getAdjustedUnitPrice(lineItem: LineItemMetricsFields) {
  if (lineItem.unit_price == null) return undefined;
  return lineItem.unit_price_type?.name === 'CPM'
    ? lineItem.unit_price * 1000
    : lineItem.unit_price;
}

export function calcUnitCost(
  lineItem: PartialLineItem,
  referralCommissionRate?: number | undefined | null
) {
  const { planned_margin } = lineItem;
  const effective_unit_price = calcEffectiveUnitPrice(lineItem, referralCommissionRate);
  if (effective_unit_price == null || planned_margin == null) return undefined;
  return effective_unit_price * (1 - planned_margin);
}

export function calcPlannedMargin(
  lineItem: PartialLineItem,
  referralCommissionRate?: number | undefined | null
): number | undefined {
  const { unit_price, planned_unit_cost } = lineItem;
  if (unit_price == null || planned_unit_cost == null) return undefined;

  const effectiveRate = referralCommissionRate ?? lineItem.referral_commission_rate ?? 0;
  const effectiveUnitPrice = unit_price * (1 - effectiveRate);

  if (effectiveUnitPrice === 0) return undefined;
  return (effectiveUnitPrice - planned_unit_cost) / effectiveUnitPrice;
}

export function calcMediaBudget(
  lineItem: PartialLineItem,
  referralCommissionRate?: number | undefined | null
) {
  if (lineItem.line_item_type === 'management_fee') return lineItem.media_budget;
  if (lineItem.line_item_type === 'zero_dollar') {
    const { estimated_units, planned_unit_cost } = lineItem;
    if (isNil(estimated_units) || isNil(planned_unit_cost)) return undefined;
    return Math.round(estimated_units * planned_unit_cost);
  }

  const { price } = lineItem;
  if (price == null) return undefined;

  // Calculate planned margin if not available or if calculated value differs
  const calculatedMargin = calcPlannedMargin(lineItem, referralCommissionRate);
  const plannedMargin =
    lineItem.planned_margin == null || calculatedMargin !== lineItem.planned_margin
      ? calculatedMargin
      : lineItem.planned_margin;

  if (plannedMargin == null) return undefined;

  const effectiveRate = referralCommissionRate ?? lineItem.referral_commission_rate ?? 0;
  // Database formula: (price * (1 - referral_commission_rate)) * (1 - planned_margin)
  return price * (1 - effectiveRate) * (1 - plannedMargin);
}

export function calcCumulativePacingBlockBudget(
  lineItem: LineItemMetricsFields,
  price: number,
  referralCommissionRate?: number | undefined | null
) {
  const { planned_margin } = lineItem;
  const effective_unit_price = calcEffectiveUnitPrice(lineItem, referralCommissionRate);
  if (effective_unit_price == null || effective_unit_price === 0 || planned_margin == null)
    return undefined;
  const estimatedUnits = Math.round(price / effective_unit_price);
  const unitCost = effective_unit_price * (1 - planned_margin);
  return estimatedUnits * unitCost;
}

export function calcIndividualBlockBudgetV2(
  block: PacingBlockV2,
  targetMargin?: number | null,
  lineItemRate?: number,
  strategyRate?: number
) {
  const rate = lineItemRate ?? strategyRate ?? 0;
  const priceValue = rate ? block.budget * (1 - rate) : block.budget;
  return priceValue * (1 - (targetMargin || 0));
}

export function calcTotalMediaBudget(
  pacingDetails: PacingBlockV2[],
  lineItemType: string,
  targetMargin?: number | null,
  lineItemRate?: number,
  strategyRate?: number
): number {
  return pacingDetails.reduce((total, block) => {
    const blockBudget =
      lineItemType === 'standard'
        ? calcIndividualBlockBudgetV2(block, targetMargin, lineItemRate, strategyRate)
        : block.budget;
    return total + blockBudget;
  }, 0);
}

export function calcReferralCommissionAmount(
  lineItem: PartialLineItem,
  referralCommissionRate?: number | undefined | null
): number | undefined {
  const { price } = lineItem;
  if (price == null) return undefined;
  const effectiveRate = referralCommissionRate ?? lineItem.referral_commission_rate ?? 0;
  if (effectiveRate === 0) return 0;
  return price * effectiveRate;
}

export function calcEffectivePrice(
  lineItem: PartialLineItem,
  referralCommissionRate?: number | undefined | null
): number | undefined {
  const { price } = lineItem;
  if (price == null) return undefined;
  const effectiveRate = referralCommissionRate ?? lineItem.referral_commission_rate ?? 0;
  if (effectiveRate === 0) return price;
  return price * (1 - effectiveRate);
}

export function calcAgencyMarkupAmount(
  lineItem: PartialLineItem,
  agencyMarkupRate?: number | undefined | null
): number | undefined {
  const { price } = lineItem;
  if (price == null) return undefined;
  const effectiveRate = agencyMarkupRate ?? lineItem.agency_markup_rate ?? 0;
  if (effectiveRate === 0) return 0;
  if (effectiveRate >= 1) return undefined;
  return price / (1 - effectiveRate) - price;
}

export function calcGrossPrice(
  lineItem: PartialLineItem,
  agencyMarkupRate?: number | undefined | null
): number | undefined {
  const { price } = lineItem;
  if (price == null) return undefined;
  const markupAmount = calcAgencyMarkupAmount(lineItem, agencyMarkupRate);
  if (markupAmount == null) return price;
  return price + markupAmount;
}

export function calcGrossUnitPrice(
  lineItem: LineItemMetricsFields,
  agencyMarkupRate?: number | undefined | null
): number | undefined {
  const { unit_price } = lineItem;
  if (unit_price == null) return undefined;

  const effectiveRate = agencyMarkupRate ?? lineItem.agency_markup_rate ?? 0;
  if (effectiveRate === 0) return 0;
  if (effectiveRate >= 1) return undefined;
  return unit_price / (1 - effectiveRate);
}

export function getUnitName(unit: UnitPriceType['name']): { full: string; abbr: string } {
  switch (unit) {
    case 'CPM':
      return { full: 'impressions', abbr: 'impr.' };
    case 'CPC':
      return { full: 'clicks', abbr: 'clicks' };
    case 'CPV':
      return { full: 'views', abbr: 'views' };
    case 'CPCV':
      return { full: 'completed views', abbr: 'comp. views' };
    case 'CPA':
      return { full: 'acquisitions', abbr: 'acq.' };
    case 'CPL':
      return { full: 'leads', abbr: 'leads' };
    case 'Flat Fee':
      return { full: '', abbr: '' };
  }
}

export function getElapsedDaysDisplay(elapsedDays: number, lengthInDays: number) {
  if (elapsedDays < 0) return 0;
  if (elapsedDays > lengthInDays) return lengthInDays.toLocaleString();
  return elapsedDays.toLocaleString();
}

export function getRemainingDaysDisplay(elapsedDays: number, lengthInDays: number) {
  if (elapsedDays < 0) return lengthInDays.toLocaleString();
  if (elapsedDays > lengthInDays) return 0;
  return (lengthInDays - elapsedDays).toLocaleString();
}

export function calcRateCardTargetUnitCost(rate: Rate) {
  return rate.unitPrice * (1 - rate.targetMargin);
}

export function isLineItemUnpriced(li: {
  line_item_type: string;
  unit_price?: number | null;
  planned_unit_cost?: number | null;
}): boolean {
  if (li.line_item_type === 'standard') return li.unit_price === 0 || li.planned_unit_cost === 0;
  if (li.line_item_type === 'management_fee') return li.planned_unit_cost === 0;
  return false;
}
